import {
	listEbooks,
	getEbook,
	createEbook,
	updateEbook,
	getRenderStatus as fetchRenderStatus,
	renderPages as renderEbookPages,
	resyncRenderCounts as resyncEbookRenderCounts,
	testDownload as testEbookDownload,
	deleteEbook,
} from '@/services/academy/ebooks.service'

export default {
	namespaced: true,
	state: {
		list: { data: [], meta: { pagination: { total: 0 } } },
		listStatus: 'init',
		current: null,
		currentStatus: 'init',
		actionStatus: 'init',
	},
	getters: {
		getList: (s) => s.list,
		getListStatus: (s) => s.listStatus,
		getCurrent: (s) => s.current,
		getCurrentStatus: (s) => s.currentStatus,
		getActionStatus: (s) => s.actionStatus,
	},
	mutations: {
		SET_LIST(state, v) { state.list = v },
		SET_LIST_STATUS(state, v) { state.listStatus = v },
		SET_CURRENT(state, v) { state.current = v },
		SET_CURRENT_STATUS(state, v) { state.currentStatus = v },
		SET_ACTION_STATUS(state, v) { state.actionStatus = v },
		PATCH_RENDER_STATUS(state, { id, renderStatus }) {
			const item = (state.list.data || []).find(e => e.id === id)
			if (item && item.ebook) {
				item.ebook = { ...item.ebook, render_status: renderStatus }
			}
		},
	},
	actions: {
		async fetchList({ commit }, params) {
			commit('SET_LIST_STATUS', 'pending')
			try {
				const { data } = await listEbooks(params)
				commit('SET_LIST', data)
				commit('SET_LIST_STATUS', 'init')
			} catch {
				commit('SET_LIST_STATUS', 'error')
			}
		},
		async fetchOne({ commit }, id) {
			commit('SET_CURRENT_STATUS', 'pending')
			try {
				const { data } = await getEbook(id)
				commit('SET_CURRENT', data.data)
				commit('SET_CURRENT_STATUS', 'init')
			} catch {
				commit('SET_CURRENT_STATUS', 'error')
			}
		},
		async create({ commit }, payload) {
			commit('SET_ACTION_STATUS', 'pending')
			try {
				const { data } = await createEbook(payload)
				commit('SET_ACTION_STATUS', 'success')
				return data
			} catch (e) {
				commit('SET_ACTION_STATUS', 'error')
				throw e
			}
		},
		async update({ commit }, { id, ...payload }) {
			commit('SET_ACTION_STATUS', 'pending')
			try {
				const { data } = await updateEbook(id, payload)
				commit('SET_ACTION_STATUS', 'success')
				return data
			} catch (e) {
				commit('SET_ACTION_STATUS', 'error')
				throw e
			}
		},
		async remove({ commit }, id) {
			commit('SET_ACTION_STATUS', 'pending')
			try {
				await deleteEbook(id)
				commit('SET_ACTION_STATUS', 'success')
			} catch (e) {
				commit('SET_ACTION_STATUS', 'error')
				throw e
			}
		},
		// Refreshes the render_status for a single list item using the same DB-based
		// source as the list transformer. This keeps the list consistent — the disk-based
		// detailed check (renderStatusDetailed) is only used on the edit page repair panel.
		async checkRenderStatus({ commit }, id) {
			const { data } = await getEbook(id)
			const renderStatus = data?.data?.ebook?.render_status || null
			if (renderStatus) {
				commit('PATCH_RENDER_STATUS', { id, renderStatus })
			}
			return renderStatus
		},
		// Recomputes the stored page counters from what is really on disk (never
		// touches missing pages), then reloads the row so the list reflects it.
		async resyncRenderCounts({ dispatch }, id) {
			try {
				await resyncEbookRenderCounts(id)
			} catch {
				// packs/bundles have no PDF detail (422): fall back to a plain re-read
			}
			return dispatch('checkRenderStatus', id)
		},
		async renderPages({ commit, dispatch }, { id, locale = null }) {
			commit('SET_ACTION_STATUS', 'pending')
			// Mark as queued immediately so the UI reflects the dispatched state
			commit('PATCH_RENDER_STATUS', { id, renderStatus: { queued: true, rendered: false, rendered_locales: 0, total_locales: 0, rendered_pages: 0, expected_pages: 0, locales: [] } })
			try {
				const { data } = await renderEbookPages(id, locale)
				commit('SET_ACTION_STATUS', 'success')
				// Refresh the item from API after queuing so the counts stay in sync
				await dispatch('checkRenderStatus', id)
				return data
			} catch (e) {
				commit('SET_ACTION_STATUS', 'error')
				throw e
			}
		},
		async testDownload(_, { id, locale = 'en' }) {
			const { data } = await testEbookDownload(id, locale)
			return data?.data || null
		},
	},
}
